<template>
  <ion-page>
    <ion-tabs :class="{ 'hide-tab-bar': isFlightDeck }">
      <ion-router-outlet></ion-router-outlet>
      <!-- Desktop sidebar: visible from 768px+ so desktop/landscape always has nav -->
      <nav class="desktop-nav" aria-label="Main navigation">
        <router-link to="/record" class="desktop-nav-link" :class="{ active: isRecord }">
          <ion-icon aria-hidden="true" :icon="airplaneOutline" />
          <span class="desktop-nav-label">Record</span>
        </router-link>
        <router-link to="/flights" class="desktop-nav-link" :class="{ active: isFlights }">
          <ion-icon aria-hidden="true" :icon="listOutline" />
          <span class="desktop-nav-label">Flights</span>
        </router-link>
        <router-link to="/flight-deck" class="desktop-nav-link" :class="{ active: isReplay }">
          <ion-icon aria-hidden="true" :icon="mapOutline" />
          <span class="desktop-nav-label">Replay</span>
        </router-link>
        <router-link to="/profile" class="desktop-nav-link" :class="{ active: isProfile }">
          <ion-icon aria-hidden="true" :icon="personCircleOutline" />
          <span class="desktop-nav-label">Profile</span>
        </router-link>
      </nav>
      <nav class="app-tab-bar" aria-label="Main navigation">
        <router-link to="/record" class="tab-btn" :class="{ active: isRecord }">
          <ion-icon aria-hidden="true" :icon="airplaneOutline" />
          <span class="tab-label">Record</span>
        </router-link>
        <router-link to="/flights" class="tab-btn" :class="{ active: isFlights }">
          <ion-icon aria-hidden="true" :icon="listOutline" />
          <span class="tab-label">Flights</span>
        </router-link>
        <router-link to="/flight-deck" class="tab-btn" :class="{ active: isReplay }">
          <ion-icon aria-hidden="true" :icon="mapOutline" />
          <span class="tab-label">Replay</span>
        </router-link>
        <router-link to="/profile" class="tab-btn" :class="{ active: isProfile }">
          <ion-icon aria-hidden="true" :icon="personCircleOutline" />
          <span class="tab-label">Profile</span>
        </router-link>
      </nav>
    </ion-tabs>
  </ion-page>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { IonTabs, IonIcon, IonPage, IonRouterOutlet } from '@ionic/vue';
import { airplaneOutline, listOutline, mapOutline, personCircleOutline } from 'ionicons/icons';
import { useReplayNotifications } from '@/composables/useReplayNotifications';
import { usePushNotifications } from '@/composables/usePushNotifications';
import { useReplayResume } from '@/composables/useReplayResume';
import { useFlightStorage } from '@/composables/useFlightStorage';

const { refreshFromServer } = useFlightStorage();
useReplayNotifications({
  onReplayReady: () => {
    void refreshFromServer();
  },
});
usePushNotifications();
useReplayResume();
const route = useRoute();
const isFlightDeck = computed(() => route.name === 'flight-deck');
const isRecord = computed(() => route.name === 'record');
const isFlights = computed(() => route.name === 'flights' || route.name === 'flight-edit');
const isReplay = computed(() => route.name === 'flight-deck');
const isProfile = computed(() => route.name === 'profile');
</script>

<style scoped>
/* Desktop sidebar: hidden on small screens */
.desktop-nav {
  display: none;
}

/* Bottom tab bar (mobile) */
.app-tab-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-around;
  min-height: 48px;
  padding: 6px 0 calc(6px + env(safe-area-inset-bottom, 0px)) 0;
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
  background: var(--ion-background-color);
  border-top: 1px solid var(--ion-border-color);
  backdrop-filter: blur(8px);
}

.tab-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px 2px;
  color: var(--ion-color-medium);
  text-decoration: none;
  font-size: 0.6rem;
  font-weight: 500;
  min-width: 0;
  transition: color 0.2s;
}

.tab-btn ion-icon {
  font-size: 22px;
  flex-shrink: 0;
}

.tab-btn.active {
  color: var(--ion-text-color);
  background: none;
}

.tab-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Hide on Flight Deck so map extends to bottom */
.hide-tab-bar .app-tab-bar {
  display: none !important;
}

/* Only hide bottom bar in landscape on narrow viewports (mobile). Desktop keeps sidebar. */
@media (orientation: landscape) and (max-width: 767px) {
  .app-tab-bar {
    display: none !important;
  }
}

/* Desktop: show sidebar, hide bottom bar, offset content */
@media (min-width: 768px) {
  .desktop-nav {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 100;
    width: 72px;
    padding: 12px 0;
    padding-top: max(12px, env(safe-area-inset-top, 0px));
    background: var(--ion-background-color);
    border-right: 1px solid var(--ion-border-color);
    backdrop-filter: blur(8px);
  }

  .desktop-nav-link {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 12px 8px;
    color: var(--ion-color-medium);
    text-decoration: none;
    font-size: 0.65rem;
    font-weight: 500;
    transition: color 0.2s, background 0.2s;
    border-radius: 8px;
    margin: 0 8px 4px;
  }

  .desktop-nav-link:hover {
    color: var(--ion-text-color);
    background: var(--ion-color-step-100);
  }

  .desktop-nav-link.active {
    color: var(--ion-text-color);
    background: var(--ion-color-step-150);
  }

  .desktop-nav-link ion-icon {
    font-size: 24px;
    flex-shrink: 0;
  }

  .desktop-nav-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  /* Offset main content so it doesn't sit under the sidebar */
  :deep(ion-router-outlet) {
    margin-left: 72px;
    min-height: 100vh;
    display: block;
  }

  .app-tab-bar {
    display: none !important;
  }
}
</style>
